import '@theme/gsap'
import { useReduced } from '@theme/maths'
import { isDesktop, mediaQueryLarge } from '@theme/use-device-detection'
import { useIntersection } from '@theme/use-intersection'

const TRACK_SELECTOR = '[data-marquee-track]'
const GROUP_SELECTOR = '[data-marquee-group]'

const MARQUEE_DEFAULTS = {
  speed: 15,
  scrollSpeed: 10,
  duplicate: 2
}

const SPEED_STEPS = [
  { below: 479, multiplier: 0.25 },
  { below: 991, multiplier: 0.5 }
]

/**
 * @typedef {object} MarqueeSettings
 * @property {number} speed
 * @property {number} scrollSpeed
 * @property {number} duplicate
 * @property {1 | -1} direction
 */

/**
 * @param {number} viewportWidth
 * @returns {number}
 */
function speedMultiplier(viewportWidth) {
  const step = SPEED_STEPS.find((candidate) => viewportWidth < candidate.below)
  return step ? step.multiplier : 1
}

/**
 * @param {string | undefined} value
 * @param {number} fallback
 * @returns {number}
 */
function toNumber(value, fallback) {
  const parsed = Number.parseFloat(value ?? '')
  return Number.isFinite(parsed) ? parsed : fallback
}

/**
 * @param {DOMStringMap} dataset
 * @returns {MarqueeSettings}
 */
function readSettings(dataset) {
  return {
    speed: toNumber(dataset.marqueeSpeed, MARQUEE_DEFAULTS.speed),
    scrollSpeed: toNumber(
      dataset.marqueeScrollSpeed,
      MARQUEE_DEFAULTS.scrollSpeed
    ),
    duplicate: toNumber(dataset.marqueeDuplicate, MARQUEE_DEFAULTS.duplicate),
    direction: dataset.marqueeDirection === 'right' ? 1 : -1
  }
}

/**
 * @param {HTMLElement} group
 * @returns {HTMLElement}
 */
function cloneGroup(group) {
  const clone = group.cloneNode(true)
  clone.setAttribute('aria-hidden', 'true')
  for (const focusable of clone.querySelectorAll('a, button')) {
    focusable.tabIndex = -1
  }
  return clone
}

class MarqueeElement extends HTMLElement {
  #track = null
  #loop = null
  #scrub = null
  #directionTrigger = null
  #baseDirection = -1
  #scrollSpeed = MARQUEE_DEFAULTS.scrollSpeed
  #stopObserving = null
  #isVisible = true

  /**
   * @param {MediaQueryListEvent} event
   * @returns {void}
   */
  #onBreakpointChange = (event) => {
    if (event.matches) {
      this.#buildScrollEffect()
      return
    }
    this.#dropScrollEffect()
  }

  /**
   * @param {IntersectionObserverEntry} entry
   * @returns {void}
   */
  #onVisibilityChange = (entry) => {
    this.#isVisible = entry.isIntersecting
    if (entry.isIntersecting) {
      this.#resumeMotion()
      return
    }
    this.#suspendMotion()
  }

  connectedCallback() {
    if (useReduced()) return

    this.#track = this.querySelector(TRACK_SELECTOR)
    const group = this.querySelector(GROUP_SELECTOR)
    if (!(this.#track && group && window.gsap && window.ScrollTrigger)) return

    const settings = readSettings(this.dataset)
    this.#baseDirection = settings.direction
    this.#scrollSpeed = settings.scrollSpeed
    this.#fillTrack(group, settings.duplicate)
    this.#startLoop(group, settings)
    if (isDesktop()) this.#buildScrollEffect()
    mediaQueryLarge.addEventListener('change', this.#onBreakpointChange)
    this.#stopObserving = useIntersection({ threshold: 0 }).observe(
      this,
      this.#onVisibilityChange
    )
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#onBreakpointChange)
    this.#stopObserving?.()
    this.#stopObserving = null
    this.#loop?.kill()
    this.#loop = null
    this.#killScrollEffect()
  }

  /**
   * @returns {void}
   */
  #buildScrollEffect() {
    if (this.#directionTrigger) return

    this.#widenTrack(this.#scrollSpeed)
    this.#watchDirection()
    this.#scrubTravel(this.#scrollSpeed)
    if (!this.#isVisible) this.#suspendMotion()
  }

  /**
   * @returns {void}
   */
  #dropScrollEffect() {
    this.#killScrollEffect()
    this.#resetTrack()
    this.dataset.marqueeStatus = 'normal'
    this.#loop?.timeScale(this.#baseDirection)
  }

  /**
   * @returns {void}
   */
  #killScrollEffect() {
    this.#scrub?.scrollTrigger?.kill()
    this.#scrub?.kill()
    this.#directionTrigger?.kill()
    this.#scrub = null
    this.#directionTrigger = null
  }

  /**
   * @returns {void}
   */
  #suspendMotion() {
    this.#directionTrigger?.disable()
    this.#scrub?.scrollTrigger?.disable()
    this.#loop?.pause()
  }

  /**
   * @returns {void}
   */
  #resumeMotion() {
    this.#directionTrigger?.enable()
    this.#scrub?.scrollTrigger?.enable()
    this.#loop?.resume()
  }

  /**
   * @param {number} scrollSpeed
   * @returns {void}
   */
  #widenTrack(scrollSpeed) {
    this.#track.style.marginInlineStart = `${scrollSpeed * -1}%`
    this.#track.style.inlineSize = `${scrollSpeed * 2 + 100}%`
  }

  /**
   * @returns {void}
   */
  #resetTrack() {
    window.gsap.set(this.#track, { clearProps: 'x,transform' })
    this.#track.style.marginInlineStart = ''
    this.#track.style.inlineSize = ''
  }

  /**
   * @param {HTMLElement} group
   * @param {number} amount
   * @returns {void}
   */
  #fillTrack(group, amount) {
    const fragment = document.createDocumentFragment()
    for (let index = 0; index < amount; index += 1) {
      fragment.appendChild(cloneGroup(group))
    }
    this.#track.appendChild(fragment)
  }

  /**
   * @param {HTMLElement} group
   * @param {MarqueeSettings} settings
   * @returns {void}
   */
  #startLoop(group, settings) {
    const groups = this.querySelectorAll(GROUP_SELECTOR)
    const widthRatio = group.offsetWidth / window.innerWidth
    const duration =
      settings.speed * widthRatio * speedMultiplier(window.innerWidth)

    this.#loop = window.gsap.to(groups, {
      xPercent: -100,
      repeat: -1,
      duration,
      ease: 'none'
    })
    this.#loop.totalProgress(0.5)
    this.#loop.timeScale(settings.direction)
  }

  /**
   * @returns {void}
   */
  #watchDirection() {
    this.#directionTrigger = window.ScrollTrigger.create({
      trigger: this,
      start: 'top bottom',
      end: 'bottom top',
      onUpdate: (self) => this.#invertOnScrollDown(self.direction === 1)
    })
  }

  /**
   * @param {number} scrollSpeed
   * @returns {void}
   */
  #scrubTravel(scrollSpeed) {
    const offset = this.#baseDirection === -1 ? scrollSpeed : scrollSpeed * -1

    this.#scrub = window.gsap.fromTo(
      this.#track,
      { x: `${offset}vw` },
      {
        x: `${offset * -1}vw`,
        ease: 'none',
        scrollTrigger: {
          trigger: this,
          start: '0% 100%',
          end: '100% 0%',
          scrub: 0
        }
      }
    )
  }

  /**
   * @param {boolean} isScrollingDown
   * @returns {void}
   */
  #invertOnScrollDown(isScrollingDown) {
    const direction = isScrollingDown
      ? this.#baseDirection * -1
      : this.#baseDirection

    this.#loop?.timeScale(direction)
    this.dataset.marqueeStatus = isScrollingDown ? 'inverted' : 'normal'
  }
}

customElements.define('ui-marquee', MarqueeElement)
